import { Box, Icon, IconButton } from "@chakra-ui/react"
import Carousel from "@components/Carousel/Carousel"
import Slide from "@components/Carousel/Slide"
import { ProductPageData } from "@type/product"
import { blurDataURLFromThumbHash } from "@utils/image"
import Image from "next/image"
import { useMemo, useState } from "react"
import { IoBookOutline } from "react-icons/io5"
import { RiScales2Fill } from "react-icons/ri"
import { RxRulerSquare } from "react-icons/rx"
import { TfiRulerAlt } from "react-icons/tfi"

const ProductImages = ({ data }: { data: ProductPageData }) => {
  const [showDimensions, setShowDimensions] = useState(false)

  const slides = useMemo(() => {
    return (
      (data.image &&
        data.image.map((image, index) => {
          return (
            <Slide
              key={index}
              px={4}
            >
              <Image
                alt={data.name}
                src={
                  image.placeholder
                    ? image.contentUrl
                    : `${process.env.NEXT_PUBLIC_IMAGE_CDN}${image.contentUrl}`
                }
                width={image.width.value}
                height={image.height.value}
                loader={({ src }) => src}
                unoptimized
                priority={index === 0}
                placeholder={"blur"}
                blurDataURL={blurDataURLFromThumbHash(
                  image.exifData?.[0].value ?? ""
                )}
                style={{
                  objectFit: "contain",
                  position: "relative",
                  maxWidth: "300px",
                }}
              />
              {showDimensions && (
                <Box
                  bg={"blackAlpha.700"}
                  position={"absolute"}
                  left={4}
                  right={4}
                  top={0}
                  bottom={0}
                >
                  <Box
                    p={2}
                    position={"relative"}
                    height={"100%"}
                    width={"100%"}
                  >
                    {data.width && data.height && data.depth && (
                      <>
                        <Icon
                          as={RxRulerSquare}
                          width={14}
                          height={14}
                          color={"white"}
                        />
                        <Box
                          color={"white"}
                          fontWeight={"700"}
                          position={"absolute"}
                          top={"0.5rem"}
                          left={"5rem"}
                          title={"Width"}
                        >
                          {data.width.value} {"cm"}
                        </Box>
                        <Box
                          color={"white"}
                          fontWeight={"700"}
                          position={"absolute"}
                          top={"9.5rem"}
                          left={"0.4rem"}
                          transform={"rotate(-90deg)"}
                          transformOrigin={"top left"}
                          title={"Height"}
                        >
                          {data.height.value} {"cm"}
                        </Box>
                        <Box
                          color={"white"}
                          fontWeight={"700"}
                          position={"absolute"}
                          top={"5rem"}
                          left={"5rem"}
                          transform={
                            "translateX(-50%) translateY(-50%) rotate(45deg)"
                          }
                          transformOrigin={"center"}
                          title={"Depth"}
                        >
                          {data.depth.value} {"cm"}
                        </Box>
                      </>
                    )}
                    {data.weight && (
                      <>
                        <Icon
                          as={RiScales2Fill}
                          width={12}
                          height={12}
                          color={"white"}
                          position={"absolute"}
                          bottom={"1rem"}
                          left={"5rem"}
                          transform={"translateX(-50%)"}
                        />
                        <Box
                          color={"white"}
                          fontWeight={"bold"}
                          position={"absolute"}
                          bottom={"4rem"}
                          left={"5rem"}
                          transform={"translateX(-50%)"}
                          title={"Weight"}
                        >
                          {data.weight.value.toFixed(0)} {"gm"}
                        </Box>
                      </>
                    )}
                    {data.numberOfPages && (
                      <>
                        <Icon
                          as={IoBookOutline}
                          width={10}
                          height={10}
                          color={"white"}
                          position={"absolute"}
                          bottom={"1rem"}
                          right={"4.4rem"}
                        />
                        <Box
                          color={"white"}
                          fontWeight={"bold"}
                          position={"absolute"}
                          bottom={"4rem"}
                          right={"3rem"}
                          title={"Weight"}
                        >
                          {data.numberOfPages} {"pages"}
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
              )}
            </Slide>
          )
        })) ||
      []
    )
  }, [data, showDimensions])

  return (
    <Box w={"full"}>
      {showDimensions && (
        <Box
          position={"absolute"}
          bg={"transparent"}
          top={0}
          bottom={0}
          left={0}
          right={0}
          zIndex={1}
          onClick={() => setShowDimensions(false)}
        />
      )}

      <Carousel
        slides={slides}
        showDots={true}
        showArrows={true}
        extraDot={
          (data.width || data.height || data.depth || data.weight) && (
            <IconButton
              aria-label={"Dimensions"}
              icon={
                <Icon
                  as={TfiRulerAlt}
                  width={6}
                  height={6}
                />
              }
              onClick={() => setShowDimensions(!showDimensions)}
              variant={"ghost"}
              size={"sm"}
              transform={"scaleY(-1)"}
              w={8}
              h={8}
            />
          )
        }
      />
    </Box>
  )
}

export default ProductImages
